import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { AvatartSize } from '@epicuro-next/ataraxia/avatar';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { ModalRef, ModalService } from '@epicuro-next/ataraxia/modal';
import { OnboardingTarget } from '@epicuro-next/constants/rule-engine/onboarding-rules';
import { OnboardingComponent } from '@epicuro-next/features/onboarding';
import {
  selectShowAccountInHeader,
  selectShowAccountTooltipInHeader,
} from '@epicuro-next/platform/configuration';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import { selectUserConnectionStatus } from '@epicuro-next/state/connect-state';
import {
  selectOnboardingEnabled,
  selectOnboardingViewed,
  selectShowConsumerOnboarding,
  selectShowOnboardingPopupOnInit,
  selectShowSalesOnboarding,
  setActiveOnboarding,
} from '@epicuro-next/state/onboarding';
import { selectUser } from '@epicuro-next/state/user-state';
import {
  selectUserMenuOpened,
  setUserMenuOpenedState,
} from '@epicuro-next/state/user-ui-state';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';

import { UserMenuRouterService } from '../service/user-menu-router.service';

@Component({
  selector: 'epc-user-menu-widget',
  templateUrl: './user-menu-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  styleUrls: ['./user-menu-widget.component.scss'],
})
export class UserMenuWidgetComponent implements OnInit, OnDestroy {
  @Input()
  public avatarSize: AvatartSize = 's';

  public user$ = this.store.pipe(select(selectUser));
  public menuOpened$ = this.store.pipe(select(selectUserMenuOpened));
  public readonly showAccountInHeader = toSignal(
    this.store.select(selectShowAccountInHeader),
  );

  private readonly showAccountTooltipInHeaderRule = toSignal(
    this.store.select(selectShowAccountTooltipInHeader),
  );

  public readonly showAccountTooltip = computed(() =>
    Boolean(
      this.showAccountInHeader() || this.showAccountTooltipInHeaderRule(),
    ),
  );

  public readonly account = toSignal(this.store.select(selectActiveAccount));

  public userConnectionStatus$ = this.store.pipe(
    select(selectUserConnectionStatus),
  );
  private isOnboardingEnabled$ = this.store.pipe(
    select(selectOnboardingEnabled),
    filter(Boolean),
  );
  private whenOnboardingNotViewed$ = this.store.pipe(
    select(selectOnboardingViewed),
    map((value) => !value),
  );

  private selectShowOnboardingPopupOnInit$ = this.store.pipe(
    select(selectShowOnboardingPopupOnInit),
  );

  private selectShowSalesOnboarding$ = this.store.pipe(
    select(selectShowSalesOnboarding),
  );

  private selectShowConsumerOnboarding$ = this.store.pipe(
    select(selectShowConsumerOnboarding),
  );

  private isDesktop$ = atxIsMobile$.pipe(map((isMobile) => !isMobile));

  private destroy$: Subject<void> = new Subject();

  private onboardingModal: ModalRef | null = null;

  constructor(
    private store: Store,
    private router: Router,
    private menuRouterService: UserMenuRouterService,
    private modalService: ModalService,
  ) {}

  public ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.handleClose(false));
    this.checkTutorialViewed();
  }

  public ngOnDestroy() {
    this.handleClose(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** method to handle aside menu closees by backdrop click
   *  @param opened boolean flag
   */
  public handleClose(opened: boolean) {
    if (!opened) {
      this.menuRouterService.go('MainUserMenu');
      this.store.dispatch(setUserMenuOpenedState({ opened: false }));
    }
  }

  public openUserMenu() {
    this.store.dispatch(setUserMenuOpenedState({ opened: true }));
  }

  public checkTutorialViewed() {
    combineLatest([
      this.isOnboardingEnabled$,
      this.selectShowOnboardingPopupOnInit$,
      this.isDesktop$,
      this.whenOnboardingNotViewed$,
    ])
      .pipe(
        filter((values) => values.every(Boolean)),
        withLatestFrom(
          this.selectShowSalesOnboarding$,
          this.selectShowConsumerOnboarding$,
          (_, showSales, showConsumer) => ({ showSales, showConsumer }),
        ),
        filter(({ showConsumer, showSales }) => showConsumer || showSales),
        takeUntil(this.destroy$),
      )
      .subscribe(({ showConsumer, showSales }) => {
        let target = null;

        if (showSales) {
          target = OnboardingTarget.SalesRep;
        } else if (showConsumer) {
          target = OnboardingTarget.Consumer;
        }

        if (target) {
          this.store.dispatch(
            setActiveOnboarding({
              target,
            }),
          );

          this.openOnboardingModal();
        }
      });
  }

  private openOnboardingModal() {
    if (this.onboardingModal) {
      this.onboardingModal.dismiss();
    }

    this.onboardingModal = this.modalService.open(OnboardingComponent, {
      backdrop: 'static',
      scrollable: false,
      size: 'xl',
      centered: true,
    });
  }
}
